/**
 * GPS travel map API client.
 * Backend: GET /maiApi/gps-history, GET /maiApi/analytics/yard/:yardNumber/devices
 */

const getApiBase = () =>
  process.env.REACT_APP_API_SERVER_URL || '';

/**
 * Fetch GPS history for one or more devices (last 6, 12, or 24 hours).
 * @param {string[]} androidIds - Device android_id list
 * @param {number} hours - 6, 12, or 24
 * @param {string} [yardNumber] - Optional: only return points in this yard (for Yard Travel map)
 * @returns {Promise<{ points: Array<{ timestamp, latitude, longitude, android_id, device_friendly_name }>, hours: number }>}
 */
export async function fetchGpsHistory(androidIds, hours = 12, yardNumber, portalEmail) {
  if (!androidIds || androidIds.length === 0) {
    throw new Error('android_ids required');
  }
  const params = new URLSearchParams();
  params.set('hours', String(hours));
  params.set('android_ids', Array.isArray(androidIds) ? androidIds.join(',') : String(androidIds));
  if (yardNumber != null && String(yardNumber).trim() !== '') {
    params.set('yard_number', String(yardNumber).trim());
  }
  if (portalEmail != null && String(portalEmail).trim() !== '') {
    params.set('portal_email', String(portalEmail).trim());
  }
  const url = `${getApiBase()}/maiApi/gps-history?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.message || 'Failed to fetch GPS history');
  }
  return res.json();
}

/**
 * Fetch devices in a yard (for device selector).
 * @param {string} yardNumber - Yard number
 * @param {string} [timeRange='24 hours'] - e.g. '24 hours', '7 days'
 * @returns {Promise<{ devices: Array, yard_number: string }>}
 */
export async function fetchYardDevices(yardNumber, timeRange = '24 hours', portalEmail) {
  if (!yardNumber || !yardNumber.trim()) {
    throw new Error('yard_number required');
  }
  const params = new URLSearchParams();
  params.set('timeRange', timeRange);
  params.set('limit', '500');
  if (portalEmail != null && String(portalEmail).trim() !== '') {
    params.set('portal_email', String(portalEmail).trim());
  }
  const url = `${getApiBase()}/maiApi/analytics/yard/${encodeURIComponent(yardNumber.trim())}/devices?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.message || 'Failed to fetch yard devices');
  }
  return res.json();
}

export default { fetchGpsHistory, fetchYardDevices };
